/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react';
import { Snackbar } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import { SuccessClip } from '../templateCard/successClip/SuccessClip';

import { useStyles } from './EditClip.styles';
import { useLocale } from 'shared/hooks/useLocale/useLocale';
import { Image, Typography } from 'shared/components';
import { ParticipantSelectionHeader } from 'app/participantSelection/header/ParticipantSelectionHeader';
import { ParticipantSelectionFooter } from 'app/participantSelection/footer/ParticipantSelectionFooter';
import { EditClipProps } from './EditClip.types';
// import { VideoStepper } from './videoStepper/VideoStepper';
import { CeremonySelection } from './ceremonySelection/CeremonySelection';
import { ClipEditSelection } from './clipSection/ClipEditSection';
import { ClipAudioSelection } from './clipAudioSection/ClipAudioSection';
import { ClipFinalSelection } from './clipFinalSection/ClipFinalSection';
import { VideoStepper } from './videoStepper/VideoStepper';

export type EventIdParamType = { participantId: string };

export const EditClip = ({
  name,
  image,
  isMatched,
  startTime,
  audioEndTime,
  streamURL,
  audioURL,
  userId,
  firstName,
  lastName,
  renderLimit,
  isFromSelfOnBoard,
  eventId,
  isLinkedInUser,
  videoEndTime,
  videoStartTime,
}: EditClipProps) => {
  const styles = useStyles();
  const { formatMessage } = useLocale();
  const location = useLocation<{ isSelfVisible?: boolean }>();

  const isSelfVisible = location.state?.isSelfVisible ?? false;

  const [activeStep, setActiveStep] = useState(isSelfVisible ? 0 : 2);
  const [pausedTime, setPausedTime] = useState(null);
  const [selectedRange, setSelectedRange] = useState<number[]>([]);
  const [selectedAudioRange, setSelectedAudioRange] = useState<number[]>([]);
  const [isFromCeremony, setIsFromCeremony] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [utcTime, setUtcTime] = useState('');
  const [audioBlob, setAudioBlob] = useState('');
  const [clipDuration, setClipDuration] = useState();
  const [isAudioTimeWarningModalOpen, setIsAudioTimeWarningModalOpen] = useState<React.ReactNode | null>(null);

  const [updateAudio, setUpdateAudio] = useState(false);
  const [editAudio, setEditAudio] = useState<number[]>([]);
  const [clipAudioTime, setClipAudioTime] = useState<number>(15);

  if (isSuccess) {
    return (
      <SuccessClip
        userId={userId}
        utcTime={utcTime}
        isFromSelfOnBoard={isFromSelfOnBoard}
        eventId={eventId}
        isLinkedInUser={isLinkedInUser}
      />
    );
  }

  const steps = [
    {
      label: 'Identify',
      title: formatMessage({ id: 'studentSelection.clip.edit.title' }),
      subTitle: formatMessage({
        id: 'studentSelection.clip.edit.subtitle',
      }),
      component: (
        <CeremonySelection
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          setPausedTime={setPausedTime}
          isMatched={isMatched}
          setIsFromCeremony={setIsFromCeremony}
          streamURL={streamURL}
          participantId={userId}
        />
      ),
    },
    {
      label: 'Video',
      title: formatMessage({
        id: 'studentSelection.clip.edit.video.title',
      }),
      subTitle: formatMessage({
        id: 'studentSelection.clip.edit.video.subtitle',
      }),
      component: (
        <ClipEditSelection
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          // pausedTime={pausedTime}
          isMatched={isMatched}
          startTime={pausedTime || startTime}
          setSelectedRange={setSelectedRange}
          streamURL={streamURL}
          isFromCeremony={isFromCeremony}
          participantId={userId}
          setClipDuration={setClipDuration}
          sliderValue={selectedRange}
          setClipAudioTime={setClipAudioTime}
        />
      ),
    },
    {
      label: 'Audio',
      title: formatMessage({ id: 'studentSelection.clip.edit.audio.header' }),
      // subTitle: formatMessage({
      //   id: 'studentSelection.clip.edit.audio.subtitle',
      // }),
      component: (
        <ClipAudioSelection
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isMatched={isMatched}
          startTime={pausedTime || startTime}
          // pausedTime={pausedTime}
          setSelectedAudioRange={setSelectedAudioRange}
          streamURL={streamURL}
          audioURL={audioURL}
          setAudioBlob={setAudioBlob}
          clipDuration={clipDuration}
          setSnackbarMessage={(message: React.ReactNode) => setIsAudioTimeWarningModalOpen(message)}
          setClipDuration={setClipDuration}
          audioSliderValue={selectedAudioRange}
          sliderValue={selectedRange}
          setSelectedRange={setSelectedRange}
          isFromCeremony={isFromCeremony}
          updateAudio={updateAudio}
          setUpdateAudio={setUpdateAudio}
          editAudio={editAudio}
          setEditAudio={setEditAudio}
          clipAudioTime={clipAudioTime}
        />
      ),
    },
    {
      label: 'Confirm',
      title: formatMessage({ id: 'studentSelection.clip.edit.submit.title' }),
      subTitle: formatMessage({ id: 'studentSelection.clip.success.subtitle' }),
      component: (
        <ClipFinalSelection
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          audioStartTime={startTime || pausedTime}
          audioEndTime={audioEndTime}
          isMatched={isMatched}
          audioPausedTime={startTime || pausedTime}
          sliderValue={selectedRange}
          audioSliderValue={selectedAudioRange}
          streamURL={streamURL}
          setIsSuccess={setIsSuccess}
          audioURL={audioURL}
          audioBlob={audioBlob}
          defaultFirstName={firstName}
          defaultLastName={lastName}
          setUtcTime={setUtcTime}
          isFromCeremony={isFromCeremony}
          clipAudioTime={clipAudioTime}
          setSnackbarMessage={(message: React.ReactNode) => setIsAudioTimeWarningModalOpen(message)}
          renderLimit={renderLimit}
          participantId={userId}
          videoStartSeconds={videoStartTime}
          videoEndSeconds={videoEndTime}
        />
      ),
    },
  ];

  const filteredSteps = steps.filter(step => !(step.label === 'Identify' && isMatched));

  const handleSnackBarClose = () => {
    setIsAudioTimeWarningModalOpen(null);
  };

  return (
    <>
      <div>
        <ParticipantSelectionHeader studentName={name || ''} institutionLogo={image || ''} />
        <div className={styles.container}>
          <div className={styles.mainContainer}>
            <div className={styles.logoContainer}>
              <Image className={styles.logoImage} alt="" src={image || ''} />
            </div>

            <div>
              {!isSuccess && (
                <div>
                  <Typography className={styles.rootText}>{filteredSteps[activeStep].title}</Typography>
                  <Typography className={styles.subTitle}>{filteredSteps[activeStep].subTitle}</Typography>
                  <VideoStepper activeStep={activeStep} isMatched={isMatched} steps={filteredSteps} />
                  {filteredSteps[activeStep].component}
                </div>
              )}
            </div>
            <div className={styles.footerContainer}>
              <ParticipantSelectionFooter />
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={!!isAudioTimeWarningModalOpen}
        onClose={handleSnackBarClose}
        message={isAudioTimeWarningModalOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        className={styles.snackBar}
        autoHideDuration={5000}
      />
    </>
  );
};
